'use client'

import { RecordSelf } from '@/components/record-self'
import { isRecordable } from '@/lib/record-self'
import { useSyncExternalStore, type MouseEvent } from 'react'
import { AudioOrbIcon } from '@/components/audio-orb-icon'
import { useHeard } from '@/components/listen-buttons'
import { getSpeakState, speakId, speakTurkish, stopSpeaking, subscribeSpeak } from '@/lib/speak'

// Кнопки озвучки турецкого текста: 3D-значки (AudioOrbIcon) — динамик для
// обычной скорости, черепашка — ×0.75.
// Повторное нажатие во время звучания — стоп. Inline-стили: телефон
// пользователя может держать устаревший CSS-бандл.

function useSpeakStatus(id: string) {
  return useSyncExternalStore(
    subscribeSpeak,
    () => {
      const s = getSpeakState()
      return s?.id === id ? s.status : null
    },
    () => null,
  )
}


function OneButton({
  text,
  slow,
  size,
  label,
  audioUrl,
}: {
  text: string
  slow: boolean
  size: number
  label?: string
  audioUrl?: string | null
}) {
  const id = speakId(text, slow)
  const status = useSpeakStatus(id)
  const active = status !== null
  const [heard, markHeard] = useHeard(id)
  // Кнопка без подписи — сам 3D-шарик: без своего фона/рамки, при звучании —
  // золотое кольцо вокруг, после нажатия синее (задача 83 из Telegram).
  const bare = !label

  function onClick(e: MouseEvent<HTMLButtonElement>) {
    // Кнопка бывает внутри кликабельного (чип-ответ, слово с переводом) —
    // её нажатие не должно срабатывать как нажатие родителя.
    e.stopPropagation()
    e.preventDefault()
    markHeard()
    if (active) stopSpeaking()
    else void speakTurkish(text, { slow, audioUrl })
  }

  const loading = {
    animation: status === 'loading' ? 'pulse 1s ease-in-out infinite' : undefined,
    opacity: status === 'loading' ? 0.7 : 1,
  }

  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={(e) => e.stopPropagation()}
      aria-label={active ? 'Остановить' : slow ? 'Послушать медленно' : 'Послушать'}
      title={slow ? 'Медленно ×0.75' : 'Послушать'}
      // С подписью — круглая кнопка «Слушать» как у слова урока (задача 83 из Telegram; подпись
      // только для экранного диктора с 15.10.2026): до
      // нажатия прозрачная, после нажатия синяя (data-on); фон/рамку/цвет задаёт класс.
      className={bare ? undefined : 'tk-pill-toggle tk-pill-listen tk-pill-icon'}
      data-on={(!bare && (active || heard)) || undefined}
      style={
        bare
          ? {
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
              height: size + 12,
              minWidth: size + 12,
              padding: 0,
              borderRadius: 9999,
              border: 0,
              background: 'transparent',
              boxShadow: active
                ? '0 0 0 2px var(--tk-gold-accent), 0 0 12px -2px var(--tk-gold-accent)'
                : heard
                  ? '0 0 0 2px oklch(0.6 0.19 258), 0 0 12px -2px oklch(0.6 0.19 258 / 0.8)'
                  : undefined,
              color: active ? 'var(--tk-gold-accent)' : 'oklch(0.9 0.05 230)',
              font: 'inherit',
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1,
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
              ...loading,
            }
          : { flexShrink: 0, ...loading }
      }
    >
      <AudioOrbIcon slow={slow} size={label ? 36 : size + 12} />
      {label && <span className="sr-only">{label}</span>}
    </button>
  )
}

export function SpeakButtons({
  text,
  size = 15,
  withSlow = true,
  label,
  audioUrl,
  record = false,
}: {
  text: string
  size?: number
  withSlow?: boolean
  // Микрофон "Записать себя" рядом (15.10.2026), только у коротких фраз.
  record?: boolean
  // Подпись у основной кнопки (например «Послушать» в подсказке перевода).
  label?: string
  // Готовый mp3, если он известен странице (см. speakTurkish).
  audioUrl?: string | null
}) {
  if (!text.trim()) return null
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, verticalAlign: 'middle' }}>
      <OneButton text={text} slow={false} size={size} label={label} audioUrl={audioUrl} />
      {withSlow && <OneButton text={text} slow size={size} audioUrl={audioUrl} />}
      {record && isRecordable(text) && <RecordSelf text={text} audioUrl={audioUrl} compact={label ? undefined : size + 12} />}
    </span>
  )
}
